import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@/shared/auth/AuthContext";
import {
  cancelOperation,
  completeCip,
  completeOperation,
  createBatch,
  createCip,
  createHandoff,
  createReworkOrder,
  ensureFormInstance,
  getBatch,
  getBatchesSummary,
  getEbr,
  closeEbr,
  getHold,
  getHoldsSummary,
  getOperation,
  listBatches,
  listCip,
  listHandoffs,
  listHolds,
  listMyActivity,
  listReworkOrders,
  listBatchLosses,
  getLossesSummary,
  listWorkQueue,
  pauseOperation,
  placeHold,
  releaseHold,
  recordOperationParam,
  resumeOperation,
  startOperation,
  suggestBatchStageMaterials,
  type CompleteOperationInput,
  type MesOperation,
} from "@/modules/mes/api/mesApi";
import type { ApiCreateBatchRequest } from "@tm-food/api-contract";
import { useServerListQuery } from "@/shared/list/useServerListQuery";

export function useBatchesListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["mes", "batches"],
    fetcher: (params) => listBatches(http, params),
    filterKeys: ["orderNo", "plantCode", "lineCode"] as const,
  });
}

export function useBatchesSummaryQuery(q: string) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "batches", "summary", q.trim()],
    queryFn: () => getBatchesSummary(http, q.trim() || undefined),
    placeholderData: keepPreviousData,
  });
}

export function useHoldsSummaryQuery(q: string) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "holds", "summary", q.trim()],
    queryFn: () => getHoldsSummary(http, q.trim() || undefined),
    placeholderData: keepPreviousData,
  });
}

export function useMesBatchQuery(batchCode: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "batches", batchCode],
    queryFn: () => getBatch(http, batchCode!),
    enabled: Boolean(batchCode),
  });
}

export function useBatchStageMaterialsQuery(
  batchCode: string | null,
  opts?: {
    operationCode?: string | null;
    operationSeq?: number | null;
    includeCommon?: boolean;
    locationCode?: string | null;
  },
) {
  const { http } = useAuth();
  const operationCode = opts?.operationCode ?? null;
  const operationSeq = opts?.operationSeq ?? null;
  const includeCommon = opts?.includeCommon ?? true;
  const locationCode = opts?.locationCode ?? null;
  return useQuery({
    queryKey: [
      "mes",
      "batches",
      batchCode,
      "stage",
      operationCode,
      operationSeq,
      includeCommon,
      locationCode,
    ],
    queryFn: () =>
      suggestBatchStageMaterials(http, batchCode!, {
        operationCode,
        operationSeq,
        includeCommon,
        locationCode,
      }),
    enabled: Boolean(batchCode),
  });
}

export function useMesEbrQuery(batchCode: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "ebr", batchCode],
    queryFn: () => getEbr(http, batchCode!),
    enabled: Boolean(batchCode),
  });
}

export function useCloseEbrMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchCode: string) => closeEbr(http, batchCode),
    onSuccess: (dossier) => {
      void qc.invalidateQueries({ queryKey: ["mes", "ebr", dossier.batch.batchCode] });
      void qc.invalidateQueries({
        queryKey: ["mes", "batches", dossier.batch.batchCode],
      });
      void qc.invalidateQueries({ queryKey: ["mes", "batches"] });
    },
  });
}

export function useCreateBatchMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiCreateBatchRequest) => createBatch(http, body),
    onSuccess: (b) => {
      void qc.invalidateQueries({ queryKey: ["mes", "batches"] });
      void qc.invalidateQueries({ queryKey: ["mes", "batches", b.batchCode] });
      void qc.invalidateQueries({ queryKey: ["planning", "orders"] });
    },
  });
}

export function useMesWorkQueueQuery(
  q: string,
  plantCode?: string,
  enabled = true,
) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "work-queue", plantCode ?? "all", q],
    queryFn: async () => {
      const rows = await listWorkQueue(http, plantCode);
      const needle = q.trim().toLowerCase();
      if (!needle) return rows;
      return rows.filter(
        (r) =>
          r.batchCode.toLowerCase().includes(needle) ||
          r.orderNo.toLowerCase().includes(needle) ||
          r.operationCode.toLowerCase().includes(needle) ||
          r.opName.toLowerCase().includes(needle) ||
          r.status.toLowerCase().includes(needle),
      );
    },
    enabled,
  });
}

export function useMesMyActivityQuery(plantCode?: string, take = 50) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "my-activity", plantCode ?? "all", take],
    queryFn: () => listMyActivity(http, { plantCode, take }),
  });
}

export function useMesOperationQuery(id: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "operation", id],
    queryFn: () => getOperation(http, id!),
    enabled: Boolean(id),
  });
}

/** Chi tiết từng công đoạn mẻ — dùng tab Phiếu đánh giá & QC. */
export function useMesBatchOperationsDetailQuery(
  operationIds: string[],
  enabled = true,
) {
  const { http } = useAuth();
  const queries = useQueries({
    queries: operationIds.map((id) => ({
      queryKey: ["mes", "operation", id] as const,
      queryFn: () => getOperation(http, id),
      enabled: enabled && Boolean(id),
    })),
  });

  const operations = queries
    .map((q) => q.data)
    .filter((op): op is MesOperation => Boolean(op));
  const isLoading = enabled && operationIds.length > 0 && queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const error = queries.find((q) => q.isError)?.error ?? null;

  return { operations, isLoading, isError, error, queries };
}

export function useMesHoldsQuery(batchCode: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "holds", "by-batch", batchCode],
    queryFn: async () => {
      const page = await listHolds(http, {
        page: 1,
        pageSize: 100,
        q: "",
        status: "all",
        filters: { batchCode: batchCode! },
      });
      return page.items;
    },
    enabled: Boolean(batchCode),
  });
}

export function useHoldsListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["mes", "holds"],
    fetcher: (params) => listHolds(http, params),
  });
}

export function useMesHoldQuery(holdId: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "holds", "detail", holdId],
    queryFn: () => getHold(http, holdId!),
    enabled: Boolean(holdId),
  });
}

export function useReleaseHoldMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holdId: string) => releaseHold(http, holdId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mes", "holds"] });
      void qc.invalidateQueries({ queryKey: ["mes", "batches"] });
    },
  });
}

export function useMesCipQuery(
  status: string,
  plantCode?: string,
  enabled = true,
) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "cip", plantCode ?? "all", status],
    queryFn: () =>
      listCip(http, {
        plantCode,
        status: status === "all" ? undefined : status,
      }),
    enabled,
  });
}

export function useStartOperationMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      equipmentCode,
    }: {
      id: string;
      equipmentCode?: string | null;
    }) =>
      startOperation(http, id, {
        clientRequestId: `start-${id}-${Date.now()}`,
        equipmentCode: equipmentCode?.trim() || null,
      }),
    onSuccess: (op) => {
      void qc.invalidateQueries({ queryKey: ["mes", "work-queue"] });
      void qc.invalidateQueries({ queryKey: ["mes", "my-activity"] });
      void qc.invalidateQueries({ queryKey: ["mes", "operation", op.id] });
      void qc.invalidateQueries({ queryKey: ["mes", "holds", op.batchCode] });
      void qc.invalidateQueries({ queryKey: ["mes", "cip"] });
    },
  });
}

function invalidateOp(
  qc: ReturnType<typeof useQueryClient>,
  op: { id: string; batchCode: string },
) {
  void qc.invalidateQueries({ queryKey: ["mes", "work-queue"] });
  void qc.invalidateQueries({ queryKey: ["mes", "my-activity"] });
  void qc.invalidateQueries({ queryKey: ["mes", "operation", op.id] });
  void qc.invalidateQueries({ queryKey: ["mes", "holds", op.batchCode] });
}

export function usePauseOperationMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      pauseOperation(http, id, reason),
    onSuccess: (op) => invalidateOp(qc, op),
  });
}

export function useResumeOperationMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resumeOperation(http, id),
    onSuccess: (op) => invalidateOp(qc, op),
  });
}

export function useCancelOperationMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      cancelOperation(http, id, reason),
    onSuccess: (op) => invalidateOp(qc, op),
  });
}

export function useCompleteOperationMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input?: CompleteOperationInput;
    }) => completeOperation(http, id, input),
    onSuccess: (op) => {
      void qc.invalidateQueries({ queryKey: ["mes", "work-queue"] });
      void qc.invalidateQueries({ queryKey: ["mes", "operation", op.id] });
    },
  });
}

export function usePlaceHoldMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      batchCode,
      reason,
      operationExecutionId,
    }: {
      batchCode: string;
      reason: string;
      operationExecutionId?: string;
    }) =>
      placeHold(http, batchCode, {
        reason,
        operationExecutionId: operationExecutionId ?? null,
      }),
    onSuccess: (hold) => {
      void qc.invalidateQueries({ queryKey: ["mes", "holds", hold.batchCode] });
      void qc.invalidateQueries({ queryKey: ["mes", "work-queue"] });
    },
  });
}

export function useCreateCipMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      plantCode: string;
      batchCode?: string;
      reason?: string;
    }) =>
      createCip(http, {
        plantCode: body.plantCode,
        batchCode: body.batchCode ?? null,
        reason: body.reason ?? "end_batch",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mes", "cip"] });
      void qc.invalidateQueries({ queryKey: ["mes", "work-queue"] });
    },
  });
}

export function useCompleteCipMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipId,
      result,
    }: {
      cipId: string;
      result: "Pass" | "Fail";
    }) => completeCip(http, cipId, result),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mes", "cip"] });
      void qc.invalidateQueries({ queryKey: ["mes", "work-queue"] });
    },
  });
}

export function useMesHandoffsQuery(batchCode: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "handoffs", batchCode ?? "all"],
    queryFn: () => listHandoffs(http, batchCode || undefined),
  });
}

export function useCreateHandoffMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      fromOperationId: string;
      lotCode: string;
      qty?: number;
      notes?: string;
    }) =>
      createHandoff(http, {
        fromOperationId: body.fromOperationId,
        lotCode: body.lotCode,
        qty: body.qty ?? null,
        notes: body.notes ?? null,
      }),
    onSuccess: (ho) => {
      void qc.invalidateQueries({ queryKey: ["mes", "handoffs"] });
      void qc.invalidateQueries({
        queryKey: ["mes", "operation", ho.fromOperationId],
      });
      if (ho.toOperationId) {
        void qc.invalidateQueries({
          queryKey: ["mes", "operation", ho.toOperationId],
        });
      }
      void qc.invalidateQueries({ queryKey: ["mes", "work-queue"] });
    },
  });
}

export function useMesReworksQuery() {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "reworks"],
    queryFn: () => listReworkOrders(http),
  });
}

export function useMesBatchLossesQuery(params?: {
  batchCode?: string;
  overOnly?: boolean;
  pageSize?: number;
  from?: string;
  to?: string;
  q?: string;
}, options?: { enabled?: boolean }) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "losses", params ?? {}],
    queryFn: () =>
      listBatchLosses(http, {
        batchCode: params?.batchCode,
        overOnly: params?.overOnly,
        pageSize: params?.pageSize ?? 50,
        from: params?.from,
        to: params?.to,
        q: params?.q,
      }),
    enabled: options?.enabled ?? true,
  });
}

export function useLossesListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["mes", "losses", "list"],
    filterKeys: ["from", "to"] as const,
    fetcher: (params) => listBatchLosses(http, params),
  });
}

export function useLossesSummaryQuery(
  params: {
    q: string;
    from?: string;
    to?: string;
  },
  options?: { enabled?: boolean },
) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["mes", "losses", "summary", params],
    queryFn: () =>
      getLossesSummary(http, {
        q: params.q.trim() || undefined,
        from: params.from,
        to: params.to,
      }),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateReworkMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      sourceLotCode: string;
      qty: number;
      reason: string;
      itemCode?: string;
      uomCode?: string;
      locationCode?: string;
      batchCode?: string;
    }) =>
      createReworkOrder(http, {
        sourceLotCode: body.sourceLotCode,
        qty: body.qty,
        reason: body.reason,
        itemCode: body.itemCode ?? null,
        uomCode: body.uomCode ?? null,
        locationCode: body.locationCode ?? null,
        batchCode: body.batchCode ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mes", "reworks"] });
    },
  });
}

export function useRecordParamMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      paramCode,
      value,
    }: {
      id: string;
      paramCode: string;
      value: number;
    }) => recordOperationParam(http, id, paramCode, value),
    onSuccess: (op) => {
      void qc.invalidateQueries({ queryKey: ["mes", "operation", op.id] });
      void qc.invalidateQueries({ queryKey: ["mes", "holds", op.batchCode] });
      void qc.invalidateQueries({ queryKey: ["mes", "work-queue"] });
    },
  });
}

export function useEnsureFormMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ensureFormInstance(http, id),
    onSuccess: (op) => {
      void qc.invalidateQueries({ queryKey: ["mes", "operation", op.id] });
    },
  });
}
